import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { callAPI } from '../hooks/api';
import { GlobalContext } from '../context/GlobalContext';
import { Button } from '../shadcn/Button';
import {
	ChevronDown,
	ChevronUp,
	ChevronsUpDown,
	Plus,
	Pencil,
	Trash2,
	FolderOpen,
	RefreshCw,
	Check,
	Package,
	ChevronLeft,
	ChevronRight,
	Copy,
	ExternalLink
} from 'lucide-react';
import classNames from 'classnames';
import Input from '../components/Input';
import Select from '../components/Select';
import { Dialog } from '../components/Dialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import Notice from '../components/Notice';
import Spinner from '../components/Spinner';
import { Switch } from '../shadcn/Switch';
import ProductTable, { SelectableItem, formatStorageOrRam } from '../components/ProductTable';

interface StoreProduct {
	id: string;
	price: number;
	quantity: number | null;
	available: boolean;
	product_info: {
		brand: string;
		model: string;
		device_commercial_name: string;
		display?: string;
		storage?: number;
		ram?: number;
		processor?: string;
		battery?: string;
		available_colors?: { name: string; hex: string }[];
		created_at: string;
		updated_at: string;
	};
}

interface StoreCatalog {
	catalog_id: string;
	store_email: string;
	name: string;
	description?: string | null;
	product_ids: string[];
	is_active: boolean;
	created_at: string;
	updated_at: string;
}

// Helper to generate catalog URL
function getCatalogUrl(storeName: string, catalogName: string): string {
	return `https://ventas.krece.app/s/${storeName}/${catalogName}`;
}

// Validate catalog name: only lowercase letters, numbers, and dashes (no leading/trailing dashes, no consecutive dashes)
function isValidCatalogName(name: string): boolean {
	if (!name) return false;
	return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(name);
}

// Check if name contains only allowed characters (for real-time validation feedback)
function hasOnlyAllowedChars(name: string): boolean {
	return /^[a-z0-9-]*$/.test(name);
}

function CatalogSkeleton() {
	return (
		<div className="space-y-4">
			{[...Array(2)].map((_, i) => (
				<div
					key={i}
					className="animate-pulse rounded-xl border border-neutral-800 bg-neutral-900/30 overflow-hidden"
					style={{ opacity: 1 - i * 0.3 }}
				>
					{/* Catalog header */}
					<div className="p-4 flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="size-10 rounded-lg bg-neutral-800" />
							<div className="space-y-2">
								<div className="h-4 w-32 rounded bg-neutral-800" />
								<div className="h-3 w-48 rounded bg-neutral-800/60" />
							</div>
						</div>
						<div className="flex gap-2">
							<div className="size-8 rounded bg-neutral-800/40" />
							<div className="size-8 rounded bg-neutral-800/40" />
						</div>
					</div>
					{/* Products table */}
					<div className="border-t border-neutral-800">
						{/* Table header */}
						<div className="px-4 py-2.5 flex items-center gap-4 border-b border-neutral-800/50 bg-neutral-900/50">
							<div className="w-6" /> {/* Checkbox */}
							<div className="h-2.5 w-12 rounded bg-neutral-800/60" /> {/* Brand */}
							<div className="h-2.5 w-14 rounded bg-neutral-800/60" /> {/* Model */}
							<div className="h-2.5 w-12 rounded bg-neutral-800/60" /> {/* Storage */}
							<div className="h-2.5 w-10 rounded bg-neutral-800/60" /> {/* RAM */}
							<div className="h-2.5 w-12 rounded bg-neutral-800/60" /> {/* Price */}
							<div className="h-2.5 w-10 rounded bg-neutral-800/60" /> {/* Stock */}
							<div className="h-2.5 w-12 rounded bg-neutral-800/60" /> {/* Status */}
						</div>
						{/* Table rows */}
						<div className="p-4 space-y-3">
							{[...Array(3)].map((_, j) => (
								<div key={j} className="flex items-center gap-4" style={{ opacity: 1 - j * 0.2 }}>
									<div className="size-5 rounded border-2 border-neutral-700" /> {/* Checkbox */}
									<div className="h-4 w-16 rounded bg-neutral-800/50" /> {/* Brand */}
									<div className="h-4 w-28 rounded bg-neutral-800/40" /> {/* Model */}
									<div className="h-4 w-14 rounded bg-neutral-800/35" /> {/* Storage */}
									<div className="h-4 w-12 rounded bg-neutral-800/35" /> {/* RAM */}
									<div className="h-4 w-16 rounded bg-neutral-800/30" /> {/* Price */}
									<div className="h-4 w-8 rounded bg-neutral-800/25" /> {/* Stock */}
									<div className="h-4 w-12 rounded bg-neutral-800/20" /> {/* Status */}
								</div>
							))}
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

function CatalogFormDialog({
	products,
	editingCatalog,
	onSave,
	onClose,
	loading,
	error,
	storeName
}: {
	products: StoreProduct[];
	editingCatalog: StoreCatalog | null;
	onSave: (form: { name: string; description: string; product_ids: string[] }) => Promise<void>;
	onClose: () => void;
	loading: boolean;
	error: string | null;
	storeName: string;
}) {
	const [form, setForm] = useState({ name: '', description: '', product_ids: [] as string[] });
	const [nameError, setNameError] = useState<string | null>(null);

	useEffect(() => {
		if (editingCatalog) {
			setForm({
				name: editingCatalog.name,
				description: editingCatalog.description || '',
				product_ids: editingCatalog.product_ids || []
			});
			setNameError(null); // Reset error when editing existing catalog
		} else {
			setForm({ name: '', description: '', product_ids: [] });
			setNameError(null);
		}
	}, [editingCatalog]);

	// Convert products to SelectableItem format
	const selectableItems: SelectableItem[] = useMemo(() => {
		return products.map((p) => ({
			id: p.id,
			brand: p.product_info?.brand || '',
			name: p.product_info?.device_commercial_name || '',
			storage: p.product_info?.storage,
			ram: p.product_info?.ram,
			price: p.price
		}));
	}, [products]);

	const selectedIds = useMemo(() => new Set(form.product_ids), [form.product_ids]);

	const handleSelectionChange = (ids: Set<string>) => {
		setForm((f) => ({ ...f, product_ids: Array.from(ids) }));
	};

	const handleNameChange = (value: string) => {
		// Convert to lowercase, enforce max length of 20
		const lowercased = value.toLowerCase().slice(0, 20);
		setForm((f) => ({ ...f, name: lowercased }));

		// Validate and show appropriate error
		if (!lowercased) {
			setNameError(null);
		} else if (!hasOnlyAllowedChars(lowercased)) {
			setNameError('Solo letras minúsculas, números y guiones (-)');
		} else if (!isValidCatalogName(lowercased)) {
			setNameError('No puede empezar/terminar con guión ni tener guiones consecutivos');
		} else {
			setNameError(null);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!form.name.trim() || !isValidCatalogName(form.name)) return;
		await onSave(form);
	};

	return (
		<Dialog
			title={editingCatalog ? 'Editar catálogo' : 'Nuevo catálogo'}
			onClose={onClose}
			closable
			cerrarButton={false}
			disableOverlayClose
			noContentWrapper
		>
			<form onSubmit={handleSubmit} className="flex flex-col w-[700px] min-h-0 flex-1">
				<div className="flex gap-4 pb-4">
					<div className="flex-1 space-y-1.5">
						<Input
							floatingLabel="Nombre del catálogo"
							placeholder="ej: iphones, ofertas-navidad"
							value={form.name}
							onChange={handleNameChange}
							clearable={false}
						/>
						{nameError && <p className="text-xs text-red-400">{nameError}</p>}
						{form.name && !nameError && (
							<p className="text-xs text-neutral-500">
								URL: {getCatalogUrl(storeName, form.name)}
							</p>
						)}
					</div>
					<div className="flex-1">
						<Input
							floatingLabel="Descripción (opcional)"
							placeholder="Descripción breve"
							value={form.description}
							onChange={(value) => setForm((f) => ({ ...f, description: value }))}
							clearable={false}
						/>
					</div>
				</div>

				<div className="flex-1 min-h-0 flex flex-col">
					<span className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3">
						Productos del catálogo
					</span>
					<ProductTable
						items={selectableItems}
						selectedIds={selectedIds}
						onSelectionChange={handleSelectionChange}
						emptyMessage="No tienes productos en tu inventario"
						emptyFilterMessage="No se encontraron productos con esos filtros"
						showPrice
						pageSize={10}
					/>
				</div>

				{error && <Notice message={error} variant="error" className="mt-4" />}

				<div className="flex gap-3 pt-4">
					<Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
						Cancelar
					</Button>
					<Button
						type="submit"
						className="flex-1"
						loading={loading}
						disabled={!form.name.trim() || !!nameError || form.product_ids.length === 0}
					>
						{editingCatalog ? 'Guardar' : 'Crear'}
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

type SortField = 'brand' | 'name' | 'price' | 'quantity';
type SortDirection = 'asc' | 'desc';

function SortableHeader({
	children,
	field,
	sortField,
	sortDirection,
	onSort
}: {
	children: React.ReactNode;
	field: SortField;
	sortField: SortField | null;
	sortDirection: SortDirection;
	onSort: (field: SortField) => void;
}) {
	const isActive = sortField === field;
	return (
		<button
			className="flex items-center gap-1 text-neutral-400 hover:text-neutral-200 transition-colors group"
			onClick={(e) => {
				e.stopPropagation();
				onSort(field);
			}}
		>
			<span className="text-[11px] font-semibold uppercase tracking-wider">{children}</span>
			<span className="text-neutral-500 group-hover:text-neutral-400">
				{isActive && sortDirection === 'asc' ? (
					<ChevronUp className="size-3.5" />
				) : isActive && sortDirection === 'desc' ? (
					<ChevronDown className="size-3.5" />
				) : (
					<ChevronsUpDown className="size-3.5 opacity-50" />
				)}
			</span>
		</button>
	);
}

const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50];

function CatalogCard({
	catalog,
	products,
	storeName,
	onEdit,
	onDelete,
	onRemoveProducts,
	onToggleActive,
	isTogglingActive,
	defaultExpanded = true
}: {
	catalog: StoreCatalog;
	products: StoreProduct[];
	storeName: string;
	onEdit: () => void;
	onDelete: () => void;
	onRemoveProducts: (productIds: string[]) => Promise<void>;
	onToggleActive: (active: boolean) => void;
	isTogglingActive?: boolean;
	defaultExpanded?: boolean;
}) {
	const [expanded, setExpanded] = useState(defaultExpanded);
	const [sortField, setSortField] = useState<SortField | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
	const [copied, setCopied] = useState(false);

	const catalogUrl = getCatalogUrl(storeName, catalog.name);

	const handleCopyUrl = async (e: React.MouseEvent) => {
		e.stopPropagation();
		try {
			await navigator.clipboard.writeText(catalogUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Error copying URL:', err);
		}
	};

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
		setCurrentPage(0);
	};

	const toggleSelect = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const toggleSelectAll = () => {
		if (selectedIds.size === products.length) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(products.map((p) => p.id)));
		}
	};

	const handleRemoveConfirm = async (): Promise<true | string> => {
		try {
			await onRemoveProducts(Array.from(selectedIds));
			setSelectedIds(new Set());
			setShowRemoveConfirm(false);
			return true;
		} catch (err) {
			return 'Error al eliminar productos del catálogo';
		}
	};

	const allSelected = products.length > 0 && selectedIds.size === products.length;
	const someSelected = selectedIds.size > 0 && selectedIds.size < products.length;

	const sortedProducts = useMemo(() => {
		if (!sortField) return products;
		return [...products].sort((a, b) => {
			let aVal: string | number | null;
			let bVal: string | number | null;
			switch (sortField) {
				case 'brand':
					aVal = a.product_info?.brand || '';
					bVal = b.product_info?.brand || '';
					break;
				case 'name':
					aVal = a.product_info?.device_commercial_name || '';
					bVal = b.product_info?.device_commercial_name || '';
					break;
				case 'price':
					aVal = a.price;
					bVal = b.price;
					break;
				case 'quantity':
					aVal = a.quantity ?? Infinity;
					bVal = b.quantity ?? Infinity;
					break;
				default:
					return 0;
			}
			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
			}
			return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
		});
	}, [products, sortField, sortDirection]);

	const pageCount = Math.ceil(sortedProducts.length / pageSize);
	const paginatedProducts = sortedProducts.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

	// Generate page options for Select
	const pageOptions = useMemo(() => {
		return Array.from({ length: pageCount }, (_, i) => ({
			label: String(i + 1),
			value: String(i)
		}));
	}, [pageCount]);

	// Generate page size options
	const pageSizeOptions = useMemo(() => {
		return PAGE_SIZE_OPTIONS.map((size) => ({
			label: `${size}`,
			value: String(size)
		}));
	}, []);

	return (
		<div className="rounded-xl border border-neutral-800 bg-neutral-900/30 overflow-hidden">
			{/* Header */}
			<div
				className="p-4 flex items-center justify-between cursor-pointer hover:bg-neutral-800/20 transition-colors"
				onClick={() => setExpanded(!expanded)}
			>
				<div className="flex items-center gap-3">
					<div className="size-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
						<FolderOpen className="size-5 text-primary-400" />
					</div>
					<div>
						<div className="flex items-center gap-2">
							<h3 className="font-medium text-neutral-100">{catalog.name}</h3>
							<span className="text-xs px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400">
								{products.length} {products.length === 1 ? 'producto' : 'productos'}
							</span>
						</div>
						{catalog.description && (
							<p className="text-sm text-neutral-500 mt-0.5">{catalog.description}</p>
						)}
					</div>
				</div>

				<div className="flex items-center gap-2">
					<div
						className="flex items-center gap-2 mr-2"
						onClick={(e) => e.stopPropagation()}
					>
						<span className={classNames('text-xs font-medium', catalog.is_active ? 'text-green-400' : 'text-neutral-500')}>
							{catalog.is_active ? 'Activo' : 'Inactivo'}
						</span>
						{isTogglingActive ? (
							<div className="w-11 h-6 flex items-center justify-center">
								<Spinner size={0.8} />
							</div>
						) : (
							<Switch
								checked={catalog.is_active}
								onCheckedChange={(checked) => onToggleActive(checked)}
							/>
						)}
					</div>
					<div className="h-6 w-px bg-neutral-800" />
					<button
						onClick={handleCopyUrl}
						className={classNames(
							'p-2 rounded-lg transition-colors',
							copied
								? 'bg-green-500/20 text-green-400'
								: 'hover:bg-primary-500/10 text-neutral-500 hover:text-primary-400'
						)}
						title={copied ? 'Copiado' : 'Copiar enlace'}
					>
						{copied ? <Check className="size-4" /> : <Copy className="size-4" />}
					</button>
					<a
						href={catalogUrl}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => e.stopPropagation()}
						className="p-2 rounded-lg hover:bg-neutral-800 text-neutral-500 hover:text-neutral-300 transition-colors"
						title="Abrir enlace"
					>
						<ExternalLink className="size-4" />
					</a>
					<button
						onClick={(e) => {
							e.stopPropagation();
							onEdit();
						}}
						className="p-2 rounded-lg hover:bg-neutral-800 text-neutral-500 hover:text-neutral-300 transition-colors"
						title="Editar"
					>
						<Pencil className="size-4" />
					</button>
					<button
						onClick={(e) => {
							e.stopPropagation();
							onDelete();
						}}
						className="p-2 rounded-lg hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"
						title="Eliminar"
					>
						<Trash2 className="size-4" />
					</button>
					<div className="p-2 text-neutral-500">
						{expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
					</div>
				</div>
			</div>

			{/* Products Table */}
			{expanded && (
				<div className="border-t border-neutral-800">
					{products.length === 0 ? (
						<div className="p-6 text-center text-sm text-neutral-500">
							<Package className="size-8 text-neutral-700 mx-auto mb-2" />
							No hay productos en este catálogo
						</div>
					) : (
						<>
							{/* Selection Action Bar */}
							{selectedIds.size > 0 && (
								<div className="flex items-center justify-between px-4 py-2 bg-primary-500/10 border-b border-primary-500/20">
									<span className="text-sm text-primary-300">
										{selectedIds.size} {selectedIds.size === 1 ? 'producto seleccionado' : 'productos seleccionados'}
									</span>
									<button
										onClick={(e) => {
											e.stopPropagation();
											setShowRemoveConfirm(true);
										}}
										className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm font-medium transition-colors"
									>
										<Trash2 className="size-3.5" />
										Quitar del catálogo
									</button>
								</div>
							)}
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b border-neutral-800 bg-neutral-900/50">
											<th className="pl-4 pr-2 py-2.5 w-10">
												<button
													onClick={(e) => {
														e.stopPropagation();
														toggleSelectAll();
													}}
													className={classNames(
														'size-5 rounded border-2 flex items-center justify-center transition-colors',
														allSelected
															? 'bg-primary-500 border-primary-500'
															: someSelected
																? 'bg-primary-500/50 border-primary-500'
																: 'border-neutral-600 hover:border-neutral-500'
													)}
												>
													{(allSelected || someSelected) && <Check className="size-3 text-white" />}
												</button>
											</th>
											<th className="px-4 py-2.5 text-left">
												<SortableHeader
													field="brand"
													sortField={sortField}
													sortDirection={sortDirection}
													onSort={handleSort}
												>
													Marca
												</SortableHeader>
											</th>
											<th className="px-4 py-2.5 text-left">
												<SortableHeader
													field="name"
													sortField={sortField}
													sortDirection={sortDirection}
													onSort={handleSort}
												>
													Modelo
												</SortableHeader>
											</th>
											<th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
												Almac.
											</th>
											<th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
												RAM
											</th>
											<th className="px-4 py-2.5 text-left">
												<SortableHeader
													field="price"
													sortField={sortField}
													sortDirection={sortDirection}
													onSort={handleSort}
												>
													Precio
												</SortableHeader>
											</th>
											<th className="px-4 py-2.5 text-left">
												<SortableHeader
													field="quantity"
													sortField={sortField}
													sortDirection={sortDirection}
													onSort={handleSort}
												>
													Stock
												</SortableHeader>
											</th>
											<th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
												Estado
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-neutral-800/50">
										{paginatedProducts.map((product) => {
											const isSelected = selectedIds.has(product.id);
											return (
												<tr
													key={product.id}
													onClick={(e) => {
														e.stopPropagation();
														toggleSelect(product.id);
													}}
													className={classNames(
														'cursor-pointer transition-colors',
														isSelected ? 'bg-primary-500/10 hover:bg-primary-500/15' : 'hover:bg-neutral-800/20'
													)}
												>
													<td className="pl-4 pr-2 py-2.5">
														<div
															className={classNames(
																'size-5 rounded border-2 flex items-center justify-center transition-colors',
																isSelected ? 'bg-primary-500 border-primary-500' : 'border-neutral-600'
															)}
														>
															{isSelected && <Check className="size-3 text-white" />}
														</div>
													</td>
													<td className="px-4 py-2.5 text-sm font-medium text-neutral-100">
														{product.product_info?.brand || '-'}
													</td>
													<td className="px-4 py-2.5 text-sm text-neutral-400">
														{product.product_info?.device_commercial_name || '-'}
													</td>
													<td className="px-4 py-2.5 text-sm text-neutral-400 tabular-nums">
														{formatStorageOrRam(product.product_info?.storage)}
													</td>
													<td className="px-4 py-2.5 text-sm text-neutral-400 tabular-nums">
														{formatStorageOrRam(product.product_info?.ram)}
													</td>
													<td className="px-4 py-2.5 text-sm font-medium text-neutral-100">
														${product.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
													</td>
													<td className="px-4 py-2.5 text-sm">
														<span
															className={classNames('tabular-nums', {
																'text-red-400': product.quantity === 0,
																'text-amber-400': product.quantity !== null && product.quantity > 0 && product.quantity <= 5,
																'text-neutral-400': product.quantity === null || product.quantity > 5
															})}
														>
															{product.quantity ?? '∞'}
														</span>
													</td>
													<td className="px-4 py-2.5 text-sm">
														<span
															className={classNames('text-xs font-medium', {
																'text-green-400': product.available,
																'text-neutral-500': !product.available
															})}
														>
															{product.available ? 'Activo' : 'Inactivo'}
														</span>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							{/* Pagination */}
							{pageCount > 1 && (
								<div className="flex items-center justify-between px-4 py-2.5 border-t border-neutral-800/50 bg-neutral-900/30" onClick={(e) => e.stopPropagation()}>
									<div className="flex items-center gap-2">
										<span className="text-xs text-neutral-500">
											{currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, sortedProducts.length)} de{' '}
											{sortedProducts.length}
										</span>
										<Select
											options={pageSizeOptions}
											defaultValue={String(pageSize)}
											onSelectedChange={(value) => {
												setPageSize(Number(value));
												setCurrentPage(0);
											}}
											className="!h-7 !text-xs !bg-neutral-900 !min-w-[90px]"
										/>
									</div>
									<div className="flex items-center gap-1">
										<button
											onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
											disabled={currentPage === 0}
											className="p-1.5 rounded-md hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
										>
											<ChevronLeft className="size-4" />
										</button>
										<Select
											options={pageOptions}
											defaultValue={String(currentPage)}
											onSelectedChange={(value) => setCurrentPage(Number(value))}
											className="!h-7 !text-xs !bg-neutral-900 !min-w-[50px] !w-[50px]"
										/>
										<span className="text-xs text-neutral-500">/ {pageCount}</span>
										<button
											onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
											disabled={currentPage >= pageCount - 1}
											className="p-1.5 rounded-md hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
										>
											<ChevronRight className="size-4" />
										</button>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			)}

			{/* Remove Products Confirmation */}
			{showRemoveConfirm && (
				<ConfirmDialog
					title="Quitar productos del catálogo"
					message={`¿Quitar ${selectedIds.size} ${selectedIds.size === 1 ? 'producto' : 'productos'} del catálogo "${catalog.name}"? Los productos seguirán en tu inventario.`}
					onConfirm={handleRemoveConfirm}
					onClose={() => setShowRemoveConfirm(false)}
				/>
			)}
		</div>
	);
}

export default function Catalogos() {
	const { userData } = useContext(GlobalContext);

	const [catalogs, setCatalogs] = useState<StoreCatalog[]>([]);
	const [products, setProducts] = useState<StoreProduct[]>([]);
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState<string | null>(null);

	const [formOpen, setFormOpen] = useState(false);
	const [editingCatalog, setEditingCatalog] = useState<StoreCatalog | null>(null);
	const [catalogToDelete, setCatalogToDelete] = useState<StoreCatalog | null>(null);
	const [formLoading, setFormLoading] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const [togglingCatalogId, setTogglingCatalogId] = useState<string | null>(null);
	const [toggleError, setToggleError] = useState<string | null>(null);

	// Online store name management
	const initialStoreName = userData?.online_store_name || userData?.master_store?.online_store_name || '';
	const [onlineStoreName, setOnlineStoreName] = useState(initialStoreName);
	const [storeNameInput, setStoreNameInput] = useState('');
	const [storeNameLoading, setStoreNameLoading] = useState(false);
	const [storeNameError, setStoreNameError] = useState<string | null>(null);
	const [storeNameInputError, setStoreNameInputError] = useState<string | null>(null);
	const [editingStoreName, setEditingStoreName] = useState(false);

	useEffect(() => {
		const name = userData?.online_store_name || userData?.master_store?.online_store_name || '';
		setOnlineStoreName(name);
	}, [userData?.online_store_name, userData?.master_store?.online_store_name]);

	const storeName = onlineStoreName;

	const handleStoreNameInputChange = (value: string) => {
		const lowercased = value.toLowerCase().slice(0, 60);
		setStoreNameInput(lowercased);
		if (!lowercased) {
			setStoreNameInputError(null);
		} else if (!hasOnlyAllowedChars(lowercased)) {
			setStoreNameInputError('Solo letras minúsculas, números y guiones (-)');
		} else if (!isValidCatalogName(lowercased)) {
			setStoreNameInputError('No puede empezar/terminar con guión ni tener guiones consecutivos');
		} else if (lowercased.length < 3) {
			setStoreNameInputError('Mínimo 3 caracteres');
		} else {
			setStoreNameInputError(null);
		}
	};

	const handleSaveOnlineStoreName = async () => {
		if (!storeNameInput || !isValidCatalogName(storeNameInput) || storeNameInput.length < 3) return;
		setStoreNameLoading(true);
		setStoreNameError(null);
		try {
			const [, error] = await callAPI(
				'https://api.krece.app/storeprofile/online_store_name/',
				'POST',
				{ online_store_name: storeNameInput },
				userData
			);
			if (error) {
				setStoreNameError(error);
				setStoreNameLoading(false);
				return;
			}
			setOnlineStoreName(storeNameInput);
			setEditingStoreName(false);
			setStoreNameInput('');
			setStoreNameError(null);
			setStoreNameInputError(null);
		} catch {
			setStoreNameError('Error inesperado al guardar el nombre');
		} finally {
			setStoreNameLoading(false);
		}
	};

	const fetchCatalogs = useCallback(async () => {
		setLoading(true);
		setFetchError(null);
		try {
			const [res, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_catalogs/',
				'GET',
				{},
				userData
			);
			if (error) throw new Error(error);
			setCatalogs(Array.isArray(res) ? res : (res as any)?.catalogs || []);
		} catch (err) {
			console.error('Error fetching catalogs:', err);
			setFetchError('Error al cargar los catálogos');
		} finally {
			setLoading(false);
		}
	}, [userData]);

	const fetchProducts = useCallback(async () => {
		try {
			const [res, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_products/',
				'GET',
				{ limit: 500 },
				userData
			);
			if (error) throw new Error(error);
			setProducts(Array.isArray(res) ? res : []);
		} catch (err) {
			console.error('Error fetching products:', err);
			// Products error is non-fatal - catalog cards will show "0 productos" and dialog will show empty state
		}
	}, [userData]);

	useEffect(() => {
		fetchCatalogs();
		fetchProducts();
	}, [fetchCatalogs, fetchProducts]);

	const handleOpenForm = (catalog?: StoreCatalog) => {
		setEditingCatalog(catalog || null);
		setFormOpen(true);
	};

	const handleCloseForm = () => {
		setFormLoading(false);
		setFormOpen(false);
		setEditingCatalog(null);
		setFormError(null);
	};

	const handleSaveCatalog = async (form: { name: string; description: string; product_ids: string[] }) => {
		if (!form.name.trim()) return;

		setFormLoading(true);
		setFormError(null);

		try {
			let apiError: string | null = null;

			if (editingCatalog) {
				const [, error] = await callAPI(
					'https://api.krece.app/storeprofile/store_catalogs/',
					'PUT',
					{
						catalog_id: editingCatalog.catalog_id,
						name: form.name,
						description: form.description || undefined,
						product_ids: form.product_ids
					},
					userData
				);
				apiError = error;
			} else {
				const [, error] = await callAPI(
					'https://api.krece.app/storeprofile/store_catalogs/',
					'POST',
					{
						name: form.name,
						description: form.description || undefined,
						product_ids: form.product_ids
					},
					userData
				);
				apiError = error;
			}

			if (apiError) {
				setFormError(apiError);
				setFormLoading(false);
				return;
			}

			handleCloseForm();
			fetchCatalogs();
		} catch (err) {
			console.error('Error saving catalog:', err);
			setFormError('Ha ocurrido un error inesperado');
			setFormLoading(false);
		}
	};

	const handleDeleteCatalog = async (): Promise<true | string> => {
		if (!catalogToDelete) return 'No se encontró el catálogo';
		try {
			const [, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_catalogs/',
				'DELETE',
				{ catalog_id: catalogToDelete.catalog_id },
				userData
			);
			if (error) return error;
			fetchCatalogs();
			return true;
		} catch (err) {
			console.error('Error deleting catalog:', err);
			return 'Error al eliminar el catálogo';
		}
	};

	const handleRemoveProductsFromCatalog = async (catalog: StoreCatalog, productIdsToRemove: string[]) => {
		const updatedProductIds = catalog.product_ids.filter((id) => !productIdsToRemove.includes(id));
		const [, error] = await callAPI(
			'https://api.krece.app/storeprofile/store_catalogs/',
			'PUT',
			{
				catalog_id: catalog.catalog_id,
				name: catalog.name,
				description: catalog.description || undefined,
				product_ids: updatedProductIds
			},
			userData
		);
		if (error) throw new Error(error);
		fetchCatalogs();
	};

	const handleToggleCatalogActive = async (catalog: StoreCatalog, isActive: boolean) => {
		setTogglingCatalogId(catalog.catalog_id);
		setToggleError(null);
		// Optimistic update
		setCatalogs((prev) => prev.map((c) => (c.catalog_id === catalog.catalog_id ? { ...c, is_active: isActive } : c)));

		try {
			const [, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_catalogs/',
				'PUT',
				{
					catalog_id: catalog.catalog_id,
					name: catalog.name,
					description: catalog.description || undefined,
					product_ids: catalog.product_ids,
					is_active: isActive
				},
				userData
			);
			if (error) {
				// Revert on error
				setCatalogs((prev) => prev.map((c) => (c.catalog_id === catalog.catalog_id ? { ...c, is_active: !isActive } : c)));
				setToggleError('Error al actualizar el catálogo');
				setTimeout(() => setToggleError(null), 3000);
			}
		} catch (err) {
			// Revert on error
			setCatalogs((prev) => prev.map((c) => (c.catalog_id === catalog.catalog_id ? { ...c, is_active: !isActive } : c)));
			setToggleError('Error al actualizar el catálogo');
			setTimeout(() => setToggleError(null), 3000);
		} finally {
			setTogglingCatalogId(null);
		}
	};

	// Get products for a specific catalog
	const getProductsForCatalog = useCallback(
		(catalog: StoreCatalog) => {
			return products.filter((p) => catalog.product_ids?.includes(p.id));
		},
		[products]
	);

	if (!userData?.email) return null;

	return (
		<div className="flex h-full w-full flex-col">
			{/* Header - Fixed */}
			<div className="flex-shrink-0 p-4 md:p-6 !pb-0 space-y-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<h1 className="text-2xl font-semibold tracking-tight">Catálogos</h1>
						{catalogs.length > 0 && (
							<button
								onClick={() => {
									fetchCatalogs();
									fetchProducts();
								}}
								disabled={loading}
								className="p-2 rounded-lg border border-neutral-800 bg-neutral-900/50 hover:bg-neutral-800 disabled:opacity-50 transition-colors"
								title="Recargar"
							>
								<RefreshCw
									className={classNames('size-4 text-neutral-400', loading && 'animate-spin')}
								/>
							</button>
						)}
					</div>
					{storeName && (
						<Button onClick={() => handleOpenForm()} size="sm" className="gap-1.5">
							<Plus className="size-4" />
							<span className="hidden sm:inline">Crear catálogo</span>
						</Button>
					)}
				</div>

				{/* Online store name - editable label when set */}
				{storeName && !editingStoreName && (
					<div className="flex items-center gap-2">
						<span className="text-xs text-neutral-500">Tienda online:</span>
						<span className="text-xs font-medium text-neutral-300">{storeName}</span>
						<button
							onClick={() => {
								setStoreNameInput(storeName);
								setEditingStoreName(true);
								setStoreNameError(null);
								setStoreNameInputError(null);
							}}
							className="p-1 rounded hover:bg-neutral-800 transition-colors"
							title="Editar nombre de tienda online"
						>
							<Pencil className="size-3 text-neutral-500" />
						</button>
					</div>
				)}

				{/* Inline edit for store name */}
				{storeName && editingStoreName && (
					<div className="max-w-lg space-y-1.5">
						<div className="inline-flex items-center gap-2">
							<div className="w-64 shrink-0">
								<Input
									floatingLabel="Nombre de tienda online"
									placeholder="ej: mi-tienda"
									value={storeNameInput}
									onChange={handleStoreNameInputChange}
									clearable={false}
								/>
							</div>
							<Button
								onClick={handleSaveOnlineStoreName}
								size="sm"
								className="shrink-0"
								disabled={
									storeNameLoading ||
									!storeNameInput ||
									!!storeNameInputError ||
									storeNameInput.length < 3
								}
							>
								{storeNameLoading ? <Spinner size={0.8} /> : 'Guardar'}
							</Button>
							<Button
								onClick={() => {
									setEditingStoreName(false);
									setStoreNameInput('');
									setStoreNameError(null);
									setStoreNameInputError(null);
								}}
								size="sm"
								variant="secondary"
								className="!w-auto shrink-0"
								disabled={storeNameLoading}
							>
								Cancelar
							</Button>
						</div>
						{storeNameInputError && <p className="text-xs text-red-400">{storeNameInputError}</p>}
						{storeNameError && <p className="text-xs text-red-400">{storeNameError}</p>}
						{storeNameInput && !storeNameInputError && storeNameInput.length >= 3 && (
							<p className="text-xs text-neutral-500">
								URL: https://ventas.krece.app/s/{storeNameInput}/...
							</p>
						)}
					</div>
				)}
			</div>

			{/* Content */}
			<div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
				{/* Setup online store name - shown when not yet configured */}
				{!storeName ? (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<FolderOpen className="size-16 text-neutral-700 mb-4" strokeWidth={1.5} />
						<p className="text-neutral-300 font-medium mb-1">Configura tu tienda online</p>
						<p className="text-sm text-neutral-500 mb-6 max-w-md">
							Para crear catálogos, primero configura el nombre de tu tienda online. Este nombre será
							parte de la URL de tus catálogos.
						</p>
						<div className="w-full max-w-sm space-y-3">
							<Input
								floatingLabel="Nombre de tienda online"
								placeholder="ej: mi-tienda, electronica-caracas"
								value={storeNameInput}
								onChange={handleStoreNameInputChange}
								clearable={false}
							/>
							{storeNameInputError && <p className="text-xs text-red-400">{storeNameInputError}</p>}
							{storeNameError && <p className="text-xs text-red-400">{storeNameError}</p>}
							{storeNameInput && !storeNameInputError && storeNameInput.length >= 3 && (
								<p className="text-xs text-neutral-500">
									URL: https://ventas.krece.app/s/{storeNameInput}/...
								</p>
							)}
							<Button
								onClick={handleSaveOnlineStoreName}
								size="sm"
								className="w-full gap-1.5"
								disabled={
									storeNameLoading ||
									!storeNameInput ||
									!!storeNameInputError ||
									storeNameInput.length < 3
								}
							>
								{storeNameLoading ? <Spinner size={0.8} /> : 'Guardar nombre'}
							</Button>
						</div>
					</div>
				) : loading && catalogs.length === 0 ? (
					<CatalogSkeleton />
				) : fetchError && catalogs.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<FolderOpen className="size-16 text-red-500/50 mb-4" strokeWidth={1.5} />
						<p className="text-neutral-300 font-medium mb-1">{fetchError}</p>
						<p className="text-sm text-neutral-500 mb-4">No se pudieron cargar los catálogos</p>
						<Button onClick={fetchCatalogs} size="sm" variant="secondary" className="gap-1.5">
							<RefreshCw className="size-4" />
							Reintentar
						</Button>
					</div>
				) : catalogs.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<FolderOpen className="size-16 text-neutral-700 mb-4" strokeWidth={1.5} />
						<p className="text-neutral-300 font-medium mb-1">Sin catálogos</p>
						<p className="text-sm text-neutral-500 mb-4 max-w-sm">
							Los catálogos te permiten organizar tus productos en colecciones para tu tienda online
						</p>
						<Button onClick={() => handleOpenForm()} size="sm" className="gap-1.5">
							<Plus className="size-4" />
							Crear tu primer catálogo
						</Button>
					</div>
				) : (
					<div className="space-y-4 relative">
						{loading && (
							<div className="absolute inset-0 bg-foreground/50 z-10 flex items-center justify-center rounded-lg">
								<Spinner size={1.5} />
							</div>
						)}
						{catalogs.map((catalog) => (
							<CatalogCard
								key={catalog.catalog_id}
								catalog={catalog}
								products={getProductsForCatalog(catalog)}
								storeName={storeName}
								onEdit={() => handleOpenForm(catalog)}
								onDelete={() => setCatalogToDelete(catalog)}
								onRemoveProducts={(productIds) => handleRemoveProductsFromCatalog(catalog, productIds)}
								onToggleActive={(isActive) => handleToggleCatalogActive(catalog, isActive)}
								isTogglingActive={togglingCatalogId === catalog.catalog_id}
								defaultExpanded
							/>
						))}
						{toggleError && (
							<div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-500/90 text-white text-sm font-medium rounded-lg shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200 z-50">
								{toggleError}
							</div>
						)}
					</div>
				)}
			</div>

			{/* Form Dialog */}
			{formOpen && (
				<CatalogFormDialog
					products={products}
					editingCatalog={editingCatalog}
					onSave={handleSaveCatalog}
					onClose={handleCloseForm}
					loading={formLoading}
					error={formError}
					storeName={storeName}
				/>
			)}

			{/* Delete Confirmation */}
			{catalogToDelete && (
				<ConfirmDialog
					title="Eliminar catálogo"
					message={`¿Eliminar el catálogo "${catalogToDelete.name}"? Los productos no serán eliminados.`}
					onConfirm={handleDeleteCatalog}
					onClose={() => setCatalogToDelete(null)}
				/>
			)}

		</div>
	);
}
